import React, { ReactElement, useState, useEffect, useRef } from 'react';

export interface DocSection {
  id: string;
  label: string;
  indent?: boolean;
}

interface DocLayoutProps {
  sections: DocSection[];
  children: React.ReactNode;
}

const DocLayout: React.FC<DocLayoutProps> = ({
  sections,
  children,
}): ReactElement => {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-64px 0px -55% 0px', threshold: 0 },
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      // offset for sticky navbar
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveId(id);
    }
  };

  return (
    <div className="doc_layout_shell">
      <aside className="doc_sidebar">
        <p className="doc_sidebar_heading">On this page</p>
        <nav aria-label="Page sections">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => handleClick(e, s.id)}
              className={[
                'doc_sidebar_link',
                s.indent ? 'doc_sidebar_link--indent' : '',
                activeId === s.id ? 'doc_sidebar_link--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {s.label}
            </a>
          ))}
        </nav>
      </aside>
      <div className="doc_layout_content">{children}</div>
    </div>
  );
};

export default DocLayout;
